import { TitleTypeEnum } from '@sr-sdks/api-sdk-axios';

/**
 * Presentation metadata for the MSR3 Title pages. Maps the SDK `titleType` enum
 * to the labels used in the Figma design so the List and Summary pages render
 * real values consistently. (Service-type presentation lives with the badge it
 * drives — see `serviceBadgeMeta` in `AppBadge`.)
 */

/**
 * Human label per program type. Keyed on the standalone `TitleTypeEnum` — the one
 * the search DTO accepts — rather than the `TitleTitleTypeEnum` copy inlined on
 * the `Title` model, so the values the Program type filter sends are the same
 * type the backend validates and a regen cannot let the two drift apart.
 */
export const titleTypeLabel: Record<TitleTypeEnum, string> = {
  documentary: 'Documentary',
  'feature-doc': 'Feature Doc',
  'feature-film': 'Feature Film',
  serial: 'Serial',
  'series-factual': 'Series',
  'series-fictional': 'Series',
  'short-film': 'Short Film',
  telefilm: 'Telefilm',
};

/**
 * True for series/serial program types. Tolerant of free-form values: demo /
 * legacy titles carry labels like "Series (Fictional)" that aren't enum keys,
 * so match on the word rather than the exact `TitleTypeEnum` — otherwise series
 * titles fall through to the single-title thumbnail.
 */
export const isSeriesTitleType = (type?: string) => {
  if (!type) return false;

  const value = type.toLowerCase();

  return value.includes('series') || value.includes('serial');
};

/** Title-cases a raw value, e.g. `series-fictional` → `Series Fictional`. */
const humanizeTitleType = (type: string) =>
  type
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/**
 * Human label for a program type. Uses the curated label for known enum values,
 * otherwise humanises the raw value: demo/legacy titles carry free-form types
 * (e.g. `series`, `movie`) that aren't in `TitleTypeEnum`, and — like the old
 * title page — we show those rather than nothing.
 */
export const formatTitleType = (type?: string) => {
  if (!type) return undefined;

  const known = (titleTypeLabel as Record<string, string | undefined>)[type];

  return known ?? humanizeTitleType(type);
};

/**
 * `2009 | Feature Film | Acme Films`, dropping any part that is absent. The
 * client is only known in agent workspaces, so most titles render two parts.
 */
export const formatTitleMeta = (
  year?: string,
  type?: string,
  client?: string,
) => {
  const typeText = formatTitleType(type);

  return [year, typeText, client].filter(Boolean).join(' | ');
};
